import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Banner from 'src/components/atoms/Banner';
import LanguageSpan from 'src/components/atoms/LanguageSpan';
import Title from 'src/components/atoms/Title/Title';
import CrossLinks from 'src/components/molecules/CrossLinks/Crosslinks';
import Evaluation from 'src/components/molecules/Evaluation';
import Panel from 'src/components/molecules/Panel';
import ProjectData from 'src/components/molecules/ProjectData';
import { clearSpan } from 'src/services/languageSpanHelpers';

import {
  useAction,
  useBaseUrlStore,
  useLabels,
  useLanguageStore,
  useProjectDataStore,
  useWindowWidthStore,
} from 'src/StoreDataProvider';

const ProjectView: () => JSX.Element = () => {
  const labels = useLabels();
  const { errorRandomProjects, errorProjectDetails } = useProjectDataStore();
  const params = useParams();
  const projectData = useProjectDataStore();
  const { picture, id, projectName, description, lessonsLearnt, rating } =
    projectData.projectDetails;
  const { getProject, getReportStartYearAndEndYear } = useAction();
  const windowState = useWindowWidthStore();
  const asideRef = useRef<HTMLElement>(null);
  const desktopTitleRef = useRef<HTMLHeadingElement>(null);
  const { language, firstLanguageChangeDone } = useLanguageStore();
  const { isYearView } = useBaseUrlStore();

  const positionAside = (): void => {
    if (asideRef.current) {
      if (!windowState.isMobileView) {
        if (desktopTitleRef.current) {
          const titleMargin = parseFloat(
            window
              .getComputedStyle(desktopTitleRef.current)
              .getPropertyValue('margin-top')
              .split('px')[0]
          );
          if (desktopTitleRef.current.clientHeight && titleMargin) {
            asideRef.current.style.marginTop = `-${
              desktopTitleRef.current.clientHeight + titleMargin
            }px`;
          }
        }
      } else {
        asideRef.current.style.marginTop = '0rem';
      }
    }
  };

  useEffect(() => {
    if (firstLanguageChangeDone) {
      getProject(
        params.projectId ?? '',
        language,
        windowState.isMobileView,
        getReportStartYearAndEndYear()
      );
      window.scrollTo(0, 0);
    }
  }, [params.projectId, language, firstLanguageChangeDone, isYearView]);

  useEffect(() => {
    document.title = `${
      isYearView ? labels.titleYearsView1 : labels.titleDefaultView1
    } - ${labels.project[0].toUpperCase() + labels.project.slice(1)} - ${
      !errorProjectDetails ? clearSpan(projectName) : '/'
    }`;
  }, [
    projectName,
    language,
    errorProjectDetails,
    isYearView,
    labels.titleYearsView1,
  ]);

  useEffect(() => {
    positionAside();
  }, [windowState.isMobileView, desktopTitleRef.current?.clientHeight]);

  return (
    <div>
      <Title isH1={false} />
      {errorProjectDetails ? (
        <>
          <hr aria-hidden="true" className="detail-separation"></hr>
          <div className="project-tile-error">
            <p>{labels.projectLoadingFailure}</p>
          </div>
          <hr aria-hidden="true" className="detail-separation"></hr>
        </>
      ) : (
        <div className="detail-container">
          <Banner picture={picture} />
          <hr aria-hidden="true" className="detail-separation"></hr>
          <div className="row titleForMobile overview">
            <div className="col-auto">
              <h1>
                <LanguageSpan
                  text={projectName}
                  keyPrefix={'projectNameHeader'}
                ></LanguageSpan>
              </h1>
            </div>
          </div>
          <div>
            <h1
              ref={desktopTitleRef}
              data-cy="detail-content-title"
              className="titleForDesktop"
            >
              <LanguageSpan
                text={projectName}
                keyPrefix={'detail-content-title'}
              ></LanguageSpan>
            </h1>
            <div className="row content">
              <aside ref={asideRef} className="col-auto">
                <ProjectData
                  key={id}
                  {...projectData.projectDetails}
                ></ProjectData>
                <Evaluation averageRating={rating} isOneProject={true} />
              </aside>
              <section className="col-auto">
                <div className="detail-content">
                  <div className="overview">
                    <h2
                      className="detail-content-short-description-heading"
                      data-cy="detail-content-short-description-heading"
                    >
                      {' '}
                      {labels.shortDescriptionTitle}{' '}
                    </h2>
                    <p data-cy="detail-content-short-description-text">
                      <LanguageSpan
                        text={description}
                        keyPrefix={'detail-content-short-description-text'}
                      ></LanguageSpan>
                    </p>
                    <h2 data-cy="detail-content-lessons-learnt-heading">
                      {labels.lessonsLearntTitle}
                    </h2>
                    {lessonsLearnt.length ? (
                      <ul>
                        {lessonsLearnt.map((lesson, index) => (
                          <li
                            data-cy={`detail-content-lessons-learnt-item-${index}`}
                            key={index}
                          >
                            <LanguageSpan
                              text={lesson}
                              keyPrefix={`detail-content-lessons-learnt-item-${index}`}
                            />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>{labels.noLessonsLearnt}</div>
                    )}
                    <h2 data-cy="detail-content-indicator-and-achievement-heading">
                      {labels.indicatorAndAchievement}
                    </h2>
                  </div>
                  <hr aria-hidden="true" className="detail-component"></hr>
                  <Panel projectId={projectData.projectDetails.id} />
                  <hr aria-hidden="true" className="detail-component"></hr>
                </div>
              </section>
            </div>
          </div>
          <hr aria-hidden="true" className="detail-separation"></hr>
          {!errorRandomProjects ? (
            <section className="row">
              <div className="col-auto crossComponent">
                <h2 id="crosslinks-title" className="crosslinkTitle">
                  {labels.crosslinkTitle}
                </h2>
                <CrossLinks />
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ProjectView;
